import { LauncherApiError, launcherApiService } from "../api/launcherApiService";
import { ILauncherToken } from "../interfaces/launcher";

const REFRESH_INTERVAL = 1000 * 60 * 4;
const RETRY_DELAYS = [2000, 5000, 10000, 20000];

interface ILaunchSessionCallbacks {
  onToken: (token: ILauncherToken) => void;
  onFailure: (messageKey: string, code: string) => void;
}

/**
 * Keeps the player's tokens fresh for as long as the launcher is open.
 * Brief network or server hiccups are retried; the player is only interrupted when the
 * session is really gone or the server stays unreachable.
 */
class LaunchSessionService {
  private refreshToken: string | null = null;
  private callbacks: ILaunchSessionCallbacks | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private refreshDueAt = 0;
  private refreshing = false;
  private stopped = true;

  start = (refreshToken: string, callbacks: ILaunchSessionCallbacks) => {
    this.stop();
    this.stopped = false;
    this.refreshToken = refreshToken;
    this.callbacks = callbacks;
    this.schedule();

    // Browsers throttle or freeze timers in background tabs, so a refresh can be long overdue
    // by the time the player comes back.
    document.addEventListener('visibilitychange', this.refreshIfDue);
    window.addEventListener('pageshow', this.refreshIfDue);
    window.addEventListener('online', this.refreshIfDue);
  }

  stop = () => {
    this.stopped = true;
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.refreshIfDue);
    window.removeEventListener('pageshow', this.refreshIfDue);
    window.removeEventListener('online', this.refreshIfDue);
  }

  private schedule = () => {
    clearTimeout(this.timer);
    this.refreshDueAt = Date.now() + REFRESH_INTERVAL;
    this.timer = setTimeout(this.refresh, REFRESH_INTERVAL);
  }

  private refreshIfDue = () => {
    if (document.visibilityState !== 'hidden' && Date.now() >= this.refreshDueAt) {
      this.refresh();
    }
  }

  private refresh = async () => {
    const refreshToken = this.refreshToken;
    if (this.refreshing || this.stopped || !refreshToken) {
      return;
    }

    this.refreshing = true;
    clearTimeout(this.timer);
    try {
      for (let attempt = 0; ; attempt++) {
        try {
          const token = await launcherApiService.refreshToken(refreshToken);
          if (this.stopped) return;

          this.refreshToken = token.refreshToken;
          this.schedule();
          this.callbacks?.onToken(token);
          return;
        } catch (error) {
          console.log(error);
          if (this.stopped) return;

          // The player launched a game elsewhere, which ended this session.
          if (error instanceof LauncherApiError && error.status === 409) {
            this.fail('LOGGED_IN_ELSEWHERE', 'SESSION_REPLACED');
            return;
          }

          // Refresh tokens are single-use, so a rejected one will never work again. This is usually
          // a refresh whose response was lost after the server had already rotated the token.
          const status = error instanceof LauncherApiError ? error.status : 0;
          if (error instanceof LauncherApiError && error.isRejected) {
            this.fail('SOMETHING_WENT_WRONG', `REFRESH_REJECTED_${status}`);
            return;
          }

          if (attempt >= RETRY_DELAYS.length) {
            this.fail('NETWORK_ERROR', status ? `REFRESH_HTTP_${status}` : 'REFRESH_UNREACHABLE');
            return;
          }

          await wait(RETRY_DELAYS[attempt]);
          if (this.stopped) return;
        }
      }
    } finally {
      this.refreshing = false;
    }
  }

  private fail = (messageKey: string, code: string) => {
    this.stop();
    this.callbacks?.onFailure(messageKey, code);
  }
}

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const launchSessionService = new LaunchSessionService();
